import React, { useEffect } from "react";
import { NavLink, Outlet, useLocation, Link } from "react-router-dom";
import { LayoutDashboard, Users, ShieldAlert, ScanSearch, Activity, ScrollText, ArrowLeft, LogOut, ToggleRight } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { BrandLogo } from "../../components/terminal/BrandLogo";

export const ADMIN_NAV = [
  { id: "overview", to: "/admin", end: true, icon: LayoutDashboard, label: "Overview" },
  { id: "users", to: "/admin/users", icon: Users, label: "Users" },
  { id: "features", to: "/admin/features", icon: ToggleRight, label: "Features" },
  { id: "risk", to: "/admin/risk", icon: ShieldAlert, label: "Risk Limits" },
  { id: "scanner", to: "/admin/scanner", icon: ScanSearch, label: "Scanner" },
  { id: "health", to: "/admin/health", icon: Activity, label: "System Health" },
  { id: "audit", to: "/admin/audit", icon: ScrollText, label: "Audit Log" },
];

// Super Admin Management Centre — a separate shell from the trader terminal (role === "superadmin" only).
export default function AdminShell() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const page = ADMIN_NAV.find((n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to))) ?? ADMIN_NAV[0];
  useEffect(() => { document.title = `${page.label} · Admin · Fela R.I.O`; }, [page]);
  return (
    <div className="h-screen w-screen flex bg-background text-foreground overflow-hidden" data-testid="admin-shell">
      <aside className="w-60 shrink-0 border-r border-border bg-panel flex flex-col">
        <div className="px-4 py-3 border-b border-border flex items-center gap-2">
          <BrandLogo className="h-8" testid="admin-brand-logo" />
        </div>
        <div className="px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-primary font-bold" data-testid="admin-badge">Platform Management</div>
        <nav className="flex-1 px-2 space-y-0.5">
          {ADMIN_NAV.map((n) => { const Icon = n.icon; return (
            <NavLink key={n.id} to={n.to} end={n.end} data-testid={`admin-nav-${n.id}`} className={({ isActive }) => `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm ${isActive ? "bg-primary/15 text-primary nav-active" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
              <Icon className="h-4 w-4" />{n.label}
            </NavLink>); })}
        </nav>
        <div className="p-3 border-t border-border space-y-1 text-xs">
          <div className="px-1 text-muted-foreground truncate" data-testid="admin-user">{user?.email} · <span className="text-primary font-semibold">superadmin</span></div>
          <Link to="/dashboard" data-testid="admin-to-trader" className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-secondary"><ArrowLeft className="h-3.5 w-3.5" /> Trader dashboard</Link>
          <button data-testid="admin-logout" onClick={logout} className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-secondary text-left"><LogOut className="h-3.5 w-3.5" /> Sign out</button>
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto scrollbar-thin p-5 space-y-4">
        <div className="text-[11px] text-muted-foreground" data-testid="admin-breadcrumb">Admin › <span className="text-foreground font-medium" data-testid="admin-page-title">{page.label}</span></div>
        <Outlet />
      </main>
    </div>
  );
}
